/**
 * Krok MOB1 — REOS NA TELEFONU (PWA): registrace service workeru, nabidka
 * nove verze a instalace na plochu.
 *
 * JEDNO MISTO pro vsechno kolem `navigator.serviceWorker` a instalace. Zadna
 * obrazovka nesaha na prohlizecova API primo — jednak by se dve kopie rozesly,
 * jednak se tim drzi podminka ze zadani: aplikace musi jit beze zmeny kodu
 * zabalit do nativniho obalu (Capacitor), kde service worker ani
 * `beforeinstallprompt` neexistuji. Vsechny funkce tady proto tise nic nedelaji,
 * kdyz prohlizec (nebo obal) dane API nema.
 */

/** Otisk nasazene verze z `<meta name="reos-version">` (plni ho vite z `VITE_REOS_SHA`). */
export function appVersion(): string {
  const meta = document.querySelector('meta[name="reos-version"]');
  const content = meta?.getAttribute('content') ?? '';
  // V dev rezimu zustane v HTML neprelozena znacka `%VITE_REOS_SHA%`.
  return content === '' || content.startsWith('%') ? 'dev' : content;
}

/** Bezi aplikace jako nainstalovana (plocha telefonu, nativni obal)? */
export function isStandalone(): boolean {
  // `matchMedia` chybi jen v testovacim prostredi (jsdom), ale pad kvuli
  // kosmeticke nabidce instalace by shodil cele Nastaveni.
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // iOS Safari `display-mode` v rezimu aplikace nehlasi — ma vlastni priznak.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Registrace service workeru. `onUpdateReady` se zavola, az je NOVA verze
 * stazena a ceka — prepnuti dela az uzivatel (`applyUpdate`), nikdy my sami
 * uprostred rozdelane prace.
 *
 * Adresa nese otisk verze (`?v=`), aby prohlizec po nasazeni soubor vubec
 * posoudil; se stale stejnou adresou a stejnym obsahem by zadna nova verze
 * nikdy nevznikla.
 */
export async function registerServiceWorker(onUpdateReady: () => void): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.register(`/sw.js?v=${appVersion()}`, {
      scope: '/',
    });
    // Nova verze uz mohla dojit v predchozim behu aplikace.
    if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
      onUpdateReady();
    }
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (installing === null) return;
      installing.addEventListener('statechange', () => {
        // `controller !== null` odlisi NOVOU verzi od uplne prvni instalace —
        // pri prvni instalaci neni co nabizet, uzivatel uz aktualni verzi vidi.
        if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
          onUpdateReady();
        }
      });
    });
  } catch {
    // Service worker je nadstavba (offline stranka, rychlejsi start). Kdyz
    // ho prohlizec odmitne (soukrome okno, nativni obal, http), aplikace
    // musi bezet dal a konzole zustat cista — hlidac chyb v e2e sade by
    // jinak hlasil vadu obrazovky.
  }
}

/** „Načíst" v pruhu nove verze: prepnout service worker a nacist obrazovku znovu. */
export function applyUpdate(): void {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  void navigator.serviceWorker.getRegistration().then((registration) => {
    const waiting = registration?.waiting ?? null;
    if (waiting === null) {
      window.location.reload();
      return;
    }
    waiting.postMessage({ type: 'REOS_SKIP_WAITING' });
  });
}

/* ------------------------------------------------------- instalace na plochu */

/** Udalost `beforeinstallprompt` (Chromium); v typech DOM neni. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

/**
 * Odchyti nabidku instalace. Volat jednou pri startu — prohlizec udalost
 * posle driv, nez se vykresli Nastaveni, a pak uz nikdy.
 */
export function watchInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Bez `preventDefault` si Chrome ukaze vlastni pruh; nabidku chceme na
    // JEDNOM miste (Nastavení → Vzhled), ne dvakrat.
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    for (const listener of installListeners) listener();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    for (const listener of installListeners) listener();
  });
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Prihlasi se k odberu zmen (nabidka prisla / aplikace se nainstalovala). */
export function onInstallAvailabilityChange(listener: () => void): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}

/** Vyvola dialog instalace. Vraci `true`, kdyz uzivatel instalaci potvrdil. */
export async function promptInstall(): Promise<boolean> {
  const event = deferredPrompt;
  if (event === null) return false;
  deferredPrompt = null;
  await event.prompt();
  const choice = await event.userChoice;
  for (const listener of installListeners) listener();
  return choice.outcome === 'accepted';
}

/** Jen pro testy: vrati modul do stavu pred `watchInstallPrompt`. */
export function resetInstallPromptForTests(): void {
  deferredPrompt = null;
  installListeners.clear();
}
